"use client";

import { useImmer } from "use-immer";
import { model } from "./model";
import { useContext, useState } from "react";
// eslint-disable-next-line import/named
import { FieldValues, useForm } from "react-hook-form";
import { preSubmit } from "@/forms/validation";

import { clientPatch } from "@/service/client-api/client-api";
import { UserContext } from "@/contexts/user-context";
import { toast } from "sonner";
import { ResourceContext } from "@/contexts/resource-context";
import { logEvent } from "@/service/logs/log";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import { Button } from "@/components/ui/button";
import { TextField } from "@/forms/fields/text-field/text-field";
import { LoaderIcon } from "lucide-react";
import { Form } from "@/components/ui/form";
import { FormError } from "@/forms/form-error";
import { handleUserError } from "@/service/client-api/error-handler";

export const SmsConsentForm = ({
  setIsOpenModal,
}: {
  setIsOpenModal: (isOpen: boolean) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { phoneNumber } = useContext(UserContext);
  const { logView } = useContext(ResourceContext);
  const [fields, setFields] = useImmer(phoneNumber ? {} : model);

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });

  const { handleSubmit } = form;

  const onSubmit = async ({ phoneNumber }: FieldValues) => {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    try {
      setLoading(true);

      await clientPatch(
        "profile.update",
        {},
        { phoneNumber, smsMarketingConsent: true },
      );
      logEvent("webinar_sms_modal_sign_up", logView);
      setIsOpenModal(false);
      toast.success("Zapisano!");
      setLoading(false);
    } catch (caughtError) {
      setLoading(false);
      handleUserError(caughtError, setError, setFields);
    }
  };

  return (
    <div className={"w-full max-w-[700px]"}>
      <div className={"mb-3 text-center text-xl font-semibold"}>
        Powiadomimy Cię SMS-em!
      </div>

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div
            className={
              "mx-auto mb-8 max-w-[464px] text-center text-base leading-6 font-normal"
            }
          >
            {phoneNumber ? (
              <>
                Wyrażam zgodę na przesyłanie mi wiadomości SMS na temat
                nadchodzących konferencji, na które jestem zapisana/zapisany.
              </>
            ) : (
              "Podaj swój numer telefonu, abyśmy mogli powiadomić Cię o zbliżających się konferencjach!"
            )}
          </div>

          <div className={"mx-auto w-full max-w-[300px]"}>
            {!phoneNumber && <TextField field={fields.phoneNumber} />}
          </div>

          <div className={"flex justify-center"}>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
              className={"w-full max-w-[300px]"}
            >
              {loading && <LoaderIcon className="animate-spin" />}
              {phoneNumber ? "Wyrażam zgodę" : "Zapisz"}
            </Button>
          </div>

          {!phoneNumber && (
            <div
              className={
                "mt-6 text-center text-xs leading-5 font-normal text-zinc-800 opacity-50"
              }
            >
              Zapisując się, wyrażam zgodę na przesyłanie mi wiadomości SMS na
              temat nadchodzących konferencji, na które jestem
              zapisana/zapisany.
            </div>
          )}

          <FormError error={error} />
        </form>
      </Form>
    </div>
  );
};
